import {
  Button,
  Icon,
  SearchBar,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  userManagement,
  type CreateUserResult,
  type User,
  type UserContext,
} from "../../../api/userManagement.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { PaginationControls } from "../../../components/PaginationControls/PaginationControls";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { formatDateTimeShort } from "../../../utils/dateFormatter";
import { ROUTES } from "../../../routes";
import { CreateUserModal } from "./CreateUserModal";
import styles from "../UserManagement.module.css";

type UserListContext = {
  refetchTrigger?: number;
  setToast?: (toast: { message: string; type: ToastType } | null) => void;
  onMutationSuccess?: () => void;
};

type UserListProps = {
  context: UserContext;
};

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case "verified":
      return styles.statusVerified;
    case "suspended":
      return styles.statusSuspended;
    default:
      return styles.statusPending;
  }
}

const COLUMNS: Column<User>[] = [
  { header: "Name", accessor: "name" },
  { header: "Email", accessor: "email" },
  {
    header: "Status",
    accessor: "status",
    render: (_value: unknown, user: User) => (
      <span className={`${styles.statusBadge} ${getStatusBadgeClass(user.status)}`}>
        {user.status}
      </span>
    ),
  },
  { header: "Type", accessor: "type" },
  {
    header: "Created",
    accessor: "createdAt",
    render: (_value: unknown, user: User) => formatDateTimeShort(user.createdAt),
  },
];

function UserList({ context: userContext }: UserListProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useOutletContext<UserListContext>();
  const [displayedUsers, setDisplayedUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const itemsPerPage = Number(searchParams.get("pageSize")) || 25;
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function updateURL(updates: Record<string, string>) {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      updateURL({ search: value, page: "1" });
    }, 300);
  }

  function handlePageChange(page: number) {
    setCurrentPage(page);
    updateURL({ page: String(page) });
  }

  async function loadUsers() {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await userManagement.getUsers({
        context: userContext,
        search: searchTerm || undefined,
        limit: itemsPerPage,
        offset,
      });
      setDisplayedUsers(response.users);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load users"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, [currentPage, itemsPerPage, searchParams.get("search"), userContext]);

  useEffect(() => {
    if (context.refetchTrigger !== undefined && context.refetchTrigger > 0) {
      loadUsers();
    }
  }, [context.refetchTrigger]);

  function handleRowClick(user: User) {
    navigate(ROUTES.users.detail(user.id), { replace: false });
  }

  function handleOpenCreateModal() {
    setIsCreateModalOpen(true);
  }

  function handleCloseCreateModal() {
    setIsCreateModalOpen(false);
  }

  function handleCreateSuccess(createdUser: CreateUserResult) {
    setIsCreateModalOpen(false);
    if (context.onMutationSuccess) {
      context.onMutationSuccess();
    } else {
      loadUsers();
    }
    context.setToast?.({
      message: `User ${createdUser.name} created. If they don't exist in Frontegg yet, they'll be linked on first SSO login via their email address.`,
      type: "success",
    });
  }

  function handleCreateError(error: Error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 409) return;
    context.setToast?.({
      message: `Failed to create user: ${error.message}`,
      type: "error",
    });
  }

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load"
        message={`Failed to load users: ${error.message}`}
      />
    );
  }

  return (
    <div>
      <div className={styles.searchSection}>
        <SearchBar value={searchTerm} onChange={handleSearchChange} placeholder="Search users..." />
      </div>

      <PermissionGate permission="user.write_accounts">
        <div className={styles.controlsSection}>
          <span />
          <Button variant="contained" color="primary" onClick={handleOpenCreateModal}>
            <Icon icon="plus" size="small" />
            Create user
          </Button>
        </div>
      </PermissionGate>

      <div className={styles.contentSection}>
        {loading ? (
          <LoadingState />
        ) : (
          <>
            <DataTable
              data={displayedUsers}
              columns={COLUMNS}
              onRowClick={handleRowClick}
              emptyMessage="No users found."
              rowKey="id"
            />
            <PaginationControls
              currentPage={currentPage}
              totalPages={Math.ceil(total / itemsPerPage)}
              startIndex={(currentPage - 1) * itemsPerPage}
              endIndex={currentPage * itemsPerPage}
              totalCount={total}
              itemName="users"
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateSuccess}
        onError={handleCreateError}
        context={userContext}
      />
    </div>
  );
}

export default UserList;
